import toast from 'react-hot-toast';
import { getCachedData } from './utils/offlineCache';

// Kill switch: set to false and deploy, and every browser that opens the app with signal
// unregisters the service worker and drops its caches. (If the worker itself is broken badly
// enough that the app can't load, see docs/gigrequest-offline-gig-mode-spec.md §8.)
const SERVICE_WORKER_ENABLED = true;

// The main bundle's content hash identifies this build; the worker is registered per build.
export const buildVersion = () => {
  const main = document.querySelector('script[src*="/static/js/main."]');
  return main?.getAttribute('src').match(/main\.([a-f0-9]+)\.js/)?.[1] || null;
};

async function disable() {
  try {
    for (const reg of await navigator.serviceWorker.getRegistrations()) await reg.unregister();
    for (const name of await caches.keys()) {
      if (name.startsWith('gigrequest-')) await caches.delete(name);
    }
  } catch {}
}

// Never swap versions in the middle of a gig: the prompt waits while a gig is live.
async function gigIsLive() {
  return !!(await getCachedData('activeGig'))?.data;
}

function promptUpdate(worker) {
  toast(t => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      New version available
      <button
        onClick={() => { toast.dismiss(t.id); worker.postMessage('skipWaiting'); }}
        style={{ border: 'none', borderRadius: 6, padding: '6px 10px', fontWeight: 600, cursor: 'pointer',
                 background: '#667eea', color: '#fff' }}
      >
        Update
      </button>
    </span>
  ), { id: 'sw-update', duration: Infinity });
}

// False when the page itself is already the new build, so the switch needs no reload
let reloadOnSwitch = true;

async function offerUpdate(worker, version) {
  // This page was already loaded from the network as the new build: switch over quietly.
  if (worker.scriptURL.endsWith(`v=${version}`)) {
    reloadOnSwitch = false;
    worker.postMessage('skipWaiting');
    return;
  }
  if (await gigIsLive()) return;  // it activates on its own once the app is next closed and reopened
  promptUpdate(worker);
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') return;
  if (!SERVICE_WORKER_ENABLED) { disable(); return; }
  // Only the band's side needs to open offline; audience phones on a gig or band page don't get one
  if (/^\/(gig|band)\//.test(window.location.pathname) || window.location.pathname === '/') return;
  const version = buildVersion();
  if (!version) return;

  window.addEventListener('load', async () => {
    try {
      // Ask the browser not to evict the saved app and gig data when storage runs low
      navigator.storage?.persist?.().catch(() => {});

      const hadController = !!navigator.serviceWorker.controller;
      const reg = await navigator.serviceWorker.register(`/service-worker.js?v=${version}`);

      // Reload once a new worker takes over an already-controlled page, so page and cache match
      let reloading = false;
      // (not while a gig is live: this page keeps running on what it already loaded)
      navigator.serviceWorker.addEventListener('controllerchange', async () => {
        if (!hadController || !reloadOnSwitch || reloading || await gigIsLive()) return;
        reloading = true;
        window.location.reload();
      });

      const watch = (worker) => worker.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) offerUpdate(worker, version);
      });
      if (reg.waiting && navigator.serviceWorker.controller) offerUpdate(reg.waiting, version);
      if (reg.installing) watch(reg.installing);
      reg.addEventListener('updatefound', () => watch(reg.installing));
    } catch {
      // No service worker (private browsing, unsupported): the app still works online
    }
  });
}
